import styled from '@emotion/styled';
import {
    Label,
    rem,
    TableColumnAlignment,
    TableSortDirection,
} from '@triumph/design-system';
import { ReactElement } from 'react';

interface StyledTotalHeaderProps {
    index?: number;
}

interface StyledHeaderProps {
    isSortable?: boolean;
    index?: number;
    textAlignment?: TableColumnAlignment;
}

export interface TableHeaderProps extends StyledHeaderProps {
    name?: string;
    field?: string;
    handleSort?: (column: string, sortDirection: TableSortDirection) => void;
    isTotal?: boolean;
    renderLabel?: ReactElement;
}

export const StyledHeaderContainer = styled.div<StyledHeaderProps>`
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    :hover {
        cursor: ${({ isSortable }) => (isSortable ? 'pointer' : '')};
    }
`;

export const StyledHeader = styled.th<StyledHeaderProps>`
    text-align: left;
    vertical-align: middle;
    min-width: 75px;
    border-bottom: ${({ theme }) =>
        `thin solid ${theme.colors.greyscale.gray15}`};
    border-top: ${({ theme }) =>
        `${rem(1)} solid ${theme.colors.greyscale.gray15}`};
    background: ${({ theme }) => theme.colors.secondary.actionHoverSecondary};
    padding: ${({ index }) => {
        if (index === -1) {
            return `${rem(12)} ${rem(8)} ${rem(12)} ${rem(12)}`;
        }
        if (index === 0) {
            return `${rem(12)} ${rem(8)} ${rem(12)} ${rem(8)}`;
        }

        return `${rem(12)} ${rem(8)} ${rem(12)} ${rem(8)}`;
    }};
    > div {
        [role='checkbox'] {
            padding: 0px;
        }
    }
    :hover {
        background: ${({ theme, isSortable }) =>
            isSortable
                ? theme.colors.secondary.actionPressedSecondary
                : theme.colors.secondary.actionHoverSecondary};
        cursor: ${({ isSortable }) => (isSortable ? 'pointer' : '')};
    }
    > label {
        :hover {
            cursor: ${({ isSortable }) => (isSortable ? 'pointer' : '')};
        }
    }
    :focus {
        background-color: ${({ theme }) =>
            theme.colors.secondary.actionPressedSecondary};
        outline: none;
        outline-offset: 1px;
    }
    &:last-of-type {
        padding-right: ${rem(24)};
    }
    position: sticky;
    top: 0;
    z-index: 1;
`;

const getLabelAlignment = ({
    textAlignment,
    isSortable,
}: StyledHeaderProps) => {
    if (isSortable) return 'left';

    return textAlignment === TableColumnAlignment.Right ? 'right' : 'left';
};

export const TableHeaderLabel = styled(Label)<StyledHeaderProps>`
    display: flex;
    width: 100%;
    margin: 0;
    :hover {
        cursor: ${({ isSortable }) => (isSortable ? 'pointer' : '')};
    }
    justify-content: ${getLabelAlignment};
    text-align: ${getLabelAlignment};
    font-family: "Gotham";
    font-weight: ${({ theme }) => theme.typography.fontWeights.medium};
    color: ${({ theme }) => theme.colors.greyscale.gray40};
`;

export const StyledTotalHeader = styled.th<StyledTotalHeaderProps>`
    text-align: left;
    vertical-align: middle;
    background: ${({ theme }) => theme.colors.greyscale.gray5};
    padding: ${({ index }) => {
        if (index === -1) {
            return `${rem(12)} ${rem(8)} ${rem(12)} ${rem(12)}`;
        }
        if (index === 0) {
            return `${rem(12)} ${rem(8)} ${rem(12)} ${rem(24)}`;
        }

        return `${rem(12)} ${rem(8)} ${rem(12)} ${rem(8)}`;
    }};
    &:nth-of-type(1) {
        border-radius: ${rem(4)} 0px 0px 0px;
    }
    &:last-of-type {
        border-radius: 0px ${rem(4)} 0px 0px;
        padding-right: ${rem(24)};
    }
`;

export const TableTotalLabel = styled.p`
    color: ${({ theme }) => theme.colors.greyscale.gray50};
    text-decoration-line: none;
    text-underline-offset: unset;
    font-weight: 600;
    overflow-x: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    display: inline-block;
    text-align: left;
    margin: 0;
`;

export const TableHeaderArrows = styled.div<StyledHeaderProps>`
    display: flex;
    margin: 0;
    justify-content: end;
    :hover {
        cursor: ${({ isSortable }) => (isSortable ? 'pointer' : '')};
    }
`;

export const StyledTitleHeader = styled.div`
    text-align: left;
    vertical-align: middle;
    background: ${({ theme }) => theme.colors.greyscale.gray5};
    border-radius: ${rem(4)} ${rem(4)} 0px 0px;
    font-family: "Gotham";
    font-weight: ${({ theme }) => theme.typography.fontWeights.medium};
    color: ${({ theme }) => theme.colors.greyscale.gray40};
    display: flex;
    padding: ${rem(8)} ${rem(16)};
    width: 100%;
    box-sizing: border-box;
    border-bottom: ${rem(1)} solid ${({ theme }) => theme.colors.greyscale.gray15};
`;

export const StyledActionButtonsContainer = styled.div`
    display: flex;
    flex-direction: row;
    gap: ${rem(8)};
    align-items: center;
    justify-content: flex-end;
`;
